import React from "react";
import PrePublishModal from "./PrePublishModal";
import { buildDraftSummary, type BuildDraftInput } from "./buildDraftSummary";
import { useSettingsHints } from "./useSettingsHints";
import type { SettingKey } from "./types";

interface PrePublishModalContainerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublish: () => void;
  onCancel: () => void;
  /** Inputs nécessaires pour construire le DraftSummary + hints */
  title: string;
  selectedDatesCount: number;
  timeSlotsCount: number;
  participantEmailsCount: number;
  advancedSettings: Record<string, any>;
  /** Détermine si "Adresse de copie" doit apparaître (invité sans compte). */
  isAuthenticated: boolean;
  firstSelectedDate?: string | null;
  /** Dernière date proposée : l'expiration ne peut pas la précéder. */
  lastSelectedDate?: string | null;
  /** Click "Utiliser « X » →" — applique la valeur suggérée */
  onApplySuggestion: (key: SettingKey, value: unknown) => void;
  /** Commit du SettingEditor inline — patche le state parent */
  onEdit: (key: SettingKey, value: unknown) => void;
}

/**
 * Wrapper qui calcule le `DraftSummary` et les hints à partir de l'état brut
 * du `PollCreator`, puis rend le `PrePublishModal`. Permet d'isoler les hooks
 * de calcul (`useMemo`, `useSettingsHints`) du gros composant parent.
 */
const PrePublishModalContainer: React.FC<PrePublishModalContainerProps> = ({
  open,
  onOpenChange,
  onPublish,
  onCancel,
  title,
  selectedDatesCount,
  timeSlotsCount,
  participantEmailsCount,
  advancedSettings,
  isAuthenticated,
  firstSelectedDate,
  lastSelectedDate,
  onApplySuggestion,
  onEdit,
}) => {
  const draft = React.useMemo(() => {
    const input: BuildDraftInput = {
      title,
      selectedDatesCount,
      timeSlotsCount,
      participantEmailsCount,
      advancedSettings,
      isAuthenticated,
      lastSelectedDate: lastSelectedDate ?? null,
    };
    return buildDraftSummary(input);
  }, [
    lastSelectedDate,
    title,
    selectedDatesCount,
    timeSlotsCount,
    participantEmailsCount,
    advancedSettings,
    isAuthenticated,
  ]);

  const hintsResult = useSettingsHints({
    title,
    location: (advancedSettings?.location ??
      advancedSettings?.eventLocation ??
      null) as string | null,
    expiresAt: (advancedSettings?.expiresAt ?? null) as string | null,
    audienceMode: String(advancedSettings?.audienceMode ?? "link"),
    resultsVisibility: String(advancedSettings?.resultsVisibility ?? "public"),
    sendNotifications: !!advancedSettings?.sendNotifications,
    maxResponses: (advancedSettings?.maxResponses ?? null) as number | null,
    participantEmailsCount,
    firstSelectedDate: firstSelectedDate ?? null,
  });

  return (
    <PrePublishModal
      open={open}
      onOpenChange={onOpenChange}
      draft={draft}
      onPublish={onPublish}
      onCancel={onCancel}
      hintsByKey={hintsResult.byKey}
      onApplySuggestion={onApplySuggestion}
      onEdit={onEdit}
    />
  );
};

export default PrePublishModalContainer;
